import { Box, IconButton, Switch, Tooltip } from '@mui/material';
import { styled } from '@mui/material/styles';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import EditIcon from '@mui/icons-material/Edit';
import { Client } from '../../../../data/models/client.model';
import { useCommonTranslation } from '../../../../hooks/useCommonTranslation';
import { TransportationBenefit } from '../../../../data/models/transportation-benefit.model';
import { useIsSuperReaderUser } from '../../../../hooks/permissions/useIsSuperReaderUser';

const PREFIX = 'TransportationBenefitList';

const classes = {
  container: `${PREFIX}-container`,
  transportationBenefit: `${PREFIX}-transportationBenefit`,
};

const StyledBox = styled(Box)(({ theme }) => ({
  [`&.${classes.container}`]: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'stretch',
    gap: theme.spacing(1),
  },

  [`& .${classes.transportationBenefit}`]: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    backgroundColor: '#f6cff6',
    borderRadius: 4,
    padding: theme.spacing(0.875),
  },
}));

interface TransportationBenefitListProps {
  client: Client;
  transportationBenefits: TransportationBenefit[];
  onActivate: (evt: any, transportationBenefit: TransportationBenefit) => void;
  onDeactivate: (
    evt: any,
    transportationBenefit: TransportationBenefit,
  ) => void;
  onEdit: (evt: any, transportationBenefit: TransportationBenefit) => void;
  onDelete: (evt: any, transportationBenefit: TransportationBenefit) => void;
  onStartDrag: (transportationBenefit: TransportationBenefit) => void;
  onEndDrag: (transportationBenefit: TransportationBenefit) => void;
}

export default function TransportationBenefitList({
  client,
  transportationBenefits,
  onActivate,
  onDeactivate,
  onEdit,
  onDelete,
  onStartDrag,
  onEndDrag,
}: TransportationBenefitListProps) {
  const t = useCommonTranslation();

  const isSuperReaderUser = useIsSuperReaderUser();

  const handleActiveChange = (
    evt: any,
    transportationBenefit: TransportationBenefit,
  ) => {
    if (evt.target.checked) {
      onActivate(evt, transportationBenefit);
    } else {
      // I took out this call to onDeactivate since transportation attributes work a little differently.
      // Instead we let them choose both activation dates (by callon onActivate). They can still
      // deactivate by choosing a date in the past.
      // onDeactivate(evt, transportationBenefit);
      onActivate(evt, transportationBenefit);
    }
  };

  return (
    <StyledBox className={classes.container}>
      {transportationBenefits.map((transportationBenefit) => (
        <Box
          key={transportationBenefit.transportationBenefitUUID}
          className={classes.transportationBenefit}
          draggable={transportationBenefit.state === 'active'}
          onDragStart={() => onStartDrag(transportationBenefit)}
          onDragEnd={() => onEndDrag(transportationBenefit)}>
          <Box className={classes.transportationBenefit}>
            {transportationBenefit.name}
          </Box>
          <Box>
            {!isSuperReaderUser && (
              <Tooltip title={t('client.deleteTransportationBenefit')}>
                <span>
                  <IconButton
                    data-testid="delete-transport-benefit"
                    onClick={(evt) => onDelete(evt, transportationBenefit)}
                    color="error"
                    size="large"
                    disabled={transportationBenefit.state === 'active'}>
                    <DeleteForeverIcon />
                  </IconButton>
                </span>
              </Tooltip>
            )}
            {!isSuperReaderUser && (
              <Tooltip title={t('client.editTransportationBenefit')}>
                <span>
                  <IconButton
                    data-testid="edit-transport-benefit"
                    onClick={(evt) => onEdit(evt, transportationBenefit)}
                    color="primary"
                    size="large"
                    disabled={transportationBenefit.state !== 'draft'}>
                    <EditIcon />
                  </IconButton>
                </span>
              </Tooltip>
            )}
            {!isSuperReaderUser && (
              <Tooltip
                title={
                  transportationBenefit.state === 'active'
                    ? t('client.active')
                    : t('client.inactive')
                }>
                <Switch
                  checked={transportationBenefit.state === 'active'}
                  disabled={transportationBenefit.state === 'inactive'}
                  onChange={(evt) =>
                    handleActiveChange(evt, transportationBenefit)
                  }
                />
              </Tooltip>
            )}
          </Box>
        </Box>
      ))}
    </StyledBox>
  );
}
